import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ContractTemplateService } from '@app/+authenticated/+employees/templates/contract-template/services/contract-template.service';
import { activeLocale } from '@app/+authenticated/shared/locale/locale.helper';
import { payrollSharingActive } from '@app/+authenticated/shared/payroll-sharing/payroll-providers.helper';
import { PayrollSharingStatusApiService } from '@app/+authenticated/shared/payroll-sharing/payroll-sharing-status-api.service';
import { ValidationService } from '@app/forms/validation.service';
import { selectAccountCountryCode } from '@app/reducers/account/account.selector';
import { AppState } from '@app/reducers/interfaces';
import { getAuthenticatedUser } from '@app/reducers/orm/employee/employee.selectors';
import { LanguageStartup } from '@app/startup/language-startup.service';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { ButtonComponent } from '@sb/ui/button';
import { DIALOG_DATA, DialogRef, SbDialogModule } from '@sb/ui/dialog';
import { LaddaDirective } from '@sb/ui/ladda';
import { finalize } from 'rxjs';

import { supportedHireCountryCode } from '../../constants/new-hires.const';
import {
  AddNewHirePayload,
  HireInviteSentResult,
  NewHire,
  NewHireDialogData,
  NewHireVariables,
} from '../../models/new-hires.model';
import {
  germanTriggerControls,
  useEmploymentCategoryOptions,
  useGermanTriggerControlsSync,
} from '../../services/hire-german-triggers.helper';
import { useHireSubmitErrorHandler } from '../../services/hire-submit-error.helper';
import { HireService } from '../../services/hire.service';
import { NewHiresService } from '../../services/new-hires.service';
import { HireFormComponent } from '../components/hire-form/hire-form.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'sb-hire-begin-flow-modal',
  standalone: true,
  imports: [TranslatePipe, ButtonComponent, ReactiveFormsModule, SbDialogModule, HireFormComponent, LaddaDirective],
  templateUrl: './hire-begin-flow-modal.component.html',
})
export class HireBeginFlowModalComponent implements OnInit {
  private dialogRef = inject(DialogRef<HireInviteSentResult | undefined>);
  private destroyRef = inject(DestroyRef);
  private newHiresService = inject(NewHiresService);
  private hireService = inject(HireService);
  private contractTemplateService = inject(ContractTemplateService);
  private languageStartup = inject(LanguageStartup);
  private data = inject<NewHireDialogData>(DIALOG_DATA);
  private store = inject<Store<AppState>>(Store);
  private featureService = inject(FeatureService);
  #handleHireSubmitError = useHireSubmitErrorHandler();

  loading = signal(false);

  #germanTriggers = germanTriggerControls();

  form = new FormGroup({
    first_name: new FormControl(this.data?.first_name, [Validators.required, Validators.maxLength(35)]),
    prefix_name: new FormControl(this.data?.prefix_name ?? '', [Validators.maxLength(10)]),
    last_name: new FormControl(this.data?.last_name, [Validators.required, Validators.maxLength(50)]),
    email: new FormControl(this.data?.email, [Validators.required, Validators.email, Validators.maxLength(50)]),
    phone: new FormControl(this.data?.phone || ''),
    job_title: new FormControl(this.data?.job_title, [Validators.required, Validators.maxLength(30)]),
    start_date: new FormControl(this.data?.start_date, [Validators.required]),
    country: new FormControl(supportedHireCountryCode(this.store.selectSignal(selectAccountCountryCode)()), [
      Validators.required,
    ]),
    locale: new FormControl(activeLocale(), [Validators.required]),
    team_id: new FormControl('', [Validators.required]),
    requires_guardian_signature: new FormControl(false),
    guardian_signature_count: new FormControl(1),
    ...this.#germanTriggers,
  });

  languages = this.languageStartup.getAvailableLanguages();
  hasContractTemplates = true;
  teams = this.hireService.teamsTree;
  guardianSignatureEnabled = this.featureService.isEnabled(featureFlag.TMP_GUARDIAN_SIGNATURE);
  guardianCountEnabled = this.featureService.isEnabled(featureFlag.TMP_GUARDIAN_SIGNATURE_COUNT);
  #germanReleaseEnabled = this.featureService.isEnabled(featureFlag.TMP_GERMAN_RELEASE);
  employmentCategories = useEmploymentCategoryOptions(this.#germanReleaseEnabled);

  #country = toSignal(this.form.controls.country.valueChanges, {
    initialValue: this.form.controls.country.value,
  });

  isGermanHire = computed(() => this.#germanReleaseEnabled && this.#country() === 'DE');

  #payrollSharingStatusApi = inject(PayrollSharingStatusApiService);
  #payrollFlowEnabled = this.featureService.isEnabled(featureFlag.TMP_HR_PRO_PAYROLL_FLOW);

  #payrollSharingStatus = rxResource({
    params: () => (this.#payrollFlowEnabled ? {} : undefined),
    stream: () => this.#payrollSharingStatusApi.get(),
  });

  // Hidden until a successful read confirms the notice applies — a failed read must never block the form.
  showPayrollNotice = computed(
    () => this.#payrollSharingStatus.hasValue() && payrollSharingActive(this.#payrollSharingStatus.value()),
  );

  constructor() {
    useGermanTriggerControlsSync(this.#germanTriggers, this.isGermanHire);
  }

  ngOnInit() {
    this.getContractTemplates();
    this.initEmailValidator();
  }

  private initEmailValidator() {
    this.store
      .select(getAuthenticatedUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((authenticated) => {
        const userEmail = authenticated?.email;
        this.form.controls.email?.addValidators(ValidationService.emailMatchValidator(userEmail));
        this.form.controls.email?.updateValueAndValidity();
      });
  }

  private getContractTemplates() {
    void this.contractTemplateService.getContractTemplates().subscribe((contractTemplates) => {
      this.hasContractTemplates = !!contractTemplates?.length;
    });
  }

  submit() {
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return;
    }

    // form.value excludes disabled controls, so the German triggers are present only for German hires.
    const { team_id, requires_guardian_signature, guardian_signature_count, ...variables } = this.form.value;

    const rawGuardianCount = requires_guardian_signature ? (guardian_signature_count ?? 1) : 0;
    // Clamp to single-guardian when the count feature is off (the select is hidden, but the control
    // could still hold 2), so a legacy hire never submits a count that should be unavailable.
    const guardianSignatureCount = this.guardianCountEnabled ? rawGuardianCount : Math.min(rawGuardianCount, 1);
    const payload: AddNewHirePayload = {
      team_id: team_id!,
      variables: variables as NewHireVariables,
      ...(this.guardianSignatureEnabled && { guardian_signature_count: guardianSignatureCount }),
    };

    this.loading.set(true);

    this.newHiresService
      .addNewHire(payload)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ id }: NewHire) => {
          // Without an id the invite-sent dialog has no contract-setup page to link to.
          this.dialogRef.close(
            id ? { newHireId: id, firstName: this.form.controls.first_name.value ?? '' } : undefined,
          );
        },
        error: (submitError: unknown) => this.#handleHireSubmitError(submitError, this.form),
      });
  }
}
